/* eslint max-lines: 0 */
import React, { useState, useEffect } from 'react';
import { useAsync } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';

import { WfInfoResponse, GroupForInfo, Group, Definition, Task } from 'models/response';
import { RootState } from 'models';
import { actionType } from 'utils/constants';

import utils from 'utils';
import actions from 'state/actions';
import selectors from 'state/selectors';
import services from 'services';

import { Bottom, Container } from './styles';

import CausalChoice from './CausalChoice';
import InitialStep from './InitialStep';
import Note from './Note';
import InfoStep from './InfoStep';
import StepWizard, { StepWizardChildProps } from 'react-step-wizard';

import { useHistory, useRouteMatch } from 'react-router';
import Modal from 'components/Modal';

const dropOptOne = [
  { value: 'causale', label: 'Causale' },
  { value: 'info', label: 'Info' },
];

const SendToWorkflow = () => {
  const modalProps = useSelector(selectors.modal.getModalProps);
  const { path } = useRouteMatch();
  const history = useHistory();
  const isEdit = path.split('/').pop() === 'edit';
  const dispatch = useDispatch();
  const t = utils.intl.useTranslator();
  const records = useSelector(selectors.documents.selectAllActiveDocuments);
  // assuming that all selected records have the same company
  const idCompany = records[0]?.idCompany || -1;
  const idUser = useSelector(selectors.user.getIdUser) || -1;
  const protocols = useSelector(selectors.documents.selectAllActiveDocumentsID);

  const [infoForSuspend, setInfoForSuspend] = useState<WfInfoResponse | null>(null);
  const [groupsForInfo, setGroupsForInfo] = useState<GroupForInfo[] | null>(null);
  const [definitions, setDefinitions] = useState<WfInfoResponse['definitions'] | null>(null);

  const [selectedReason, setSelectedReason] = useState<{ value: string; label: string }>(dropOptOne[0]);
  const [selectedDescription, setSelectedDescription] = useState<{ value: number; label: string } | null>(null);
  const [infoInput, setInfoInput] = useState<string>('');

  const [selectedDefinition, setSelectedDefinition] = useState<Definition | null>(null);
  const [selectedWFDefinition, setSelectedWFDefinition] = useState<GroupForInfo | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);

  // Pre selection
  const [preSelectedDefinition, setPreSelectedDefinition] = useState<Definition | null>(null);
  const [preSelectedTask, setPreSelectedTask] = useState<Task | null>(null);
  const [preSelectedGroups, setPreSelectedGroups] = useState<Group[] | null>(null);

  const [note, setNote] = useState<string>('');

  const NO_INFO_SENDTOWF =
    useSelector((store: RootState) => selectors.app.getConfig(store, actionType.NO_INFO_SENDTOWF))?.val === 1;

  // override default wizard animation
  const animationOverride = { enterRight: '', enterLeft: '', exitRight: '', exitLeft: '', intro: '' };

  const { idAction } = modalProps;

  useEffect(() => {
    if (modalProps?.data?.idCausal && infoForSuspend) {
      const preSelCausal = infoForSuspend.causals.find((e) => e.idWfCausal === modalProps.data.idCausal);
      preSelCausal && setSelectedDescription({ value: preSelCausal.idWfCausal, label: preSelCausal.causalName });
      if (modalProps?.data?.idWorkflowDefinition) {
        // Pre selection Definitions
        const preSelDefinition = infoForSuspend.definitions.find(
          (e) => e.idDefinition === modalProps.data.idWorkflowDefinition,
        );
        if (preSelDefinition) {
          setPreSelectedDefinition(preSelDefinition);
          setSelectedDefinition(preSelDefinition);
          if (modalProps?.data?.idTaskDefinition) {
            // Pre selection Tasks
            const preSelTask = preSelDefinition.tasks.find(
              (e) => e.idTaskDefinition === modalProps.data.idTaskDefinition,
            );
            if (preSelTask) {
              setPreSelectedTask(preSelTask);
              setSelectedTask(preSelTask);
              if (modalProps?.data?.idGroups.length) {
                // Pre selection Groups
                const preSelGroups: Group[] = [];
                modalProps?.data?.idGroups.map((e: number) => {
                  const group = preSelTask.groups.find((gro) => e === gro.idGroup);
                  group && preSelGroups.push(group);
                });
                if (preSelGroups.length) {
                  setPreSelectedGroups(preSelGroups);
                  setSelectedGroups(preSelGroups);
                  modalProps?.data?.note && setNote(modalProps?.data?.note);
                }
              }
            }
          }
        }
      }
    }
  }, [modalProps, infoForSuspend]);

  useAsync(async () => {
    try {
      if (idCompany === -1) return;
      const { data: dataInfo } = await services.getWfInfoForSuspend({ idCompany, idUser, idAction });
      setInfoForSuspend(dataInfo);
      const { data: dataGroups } = await services.getGroupsForInfo({ idCompany });
      setGroupsForInfo(dataGroups);
    } catch (e) {
      console.error(e);
    }
  }, [idCompany, idUser]);

  const updateSelectedTask = (isActive?: boolean, task?: Task) => {
    if (isActive && task) {
      setSelectedTask(task);
      setSelectedGroups([]);
    } else {
      setSelectedTask(null);
      setSelectedGroups([]);
    }
  };

  useEffect(() => {
    if (infoForSuspend && selectedDescription) {
      const selectedCasual = infoForSuspend.causals.find((e) => e.idWfCausal === selectedDescription.value);
      if (selectedCasual) {
        const filteredDefinitions = infoForSuspend.definitions.filter(
          (e) => e.defMaxWeight && e.defMaxWeight >= selectedCasual.weight,
        );
        setDefinitions(filteredDefinitions);
      }
    } else {
      setDefinitions(null);
    }
  }, [selectedDescription, infoForSuspend]);

  const onCancel = () => {
    dispatch(actions.modal.closeModal());
  };

  useEffect(() => {
    if (selectedReason?.value !== 'causale') return;
    if (!infoForSuspend) return;

    const [record] = infoForSuspend.causals;

    if (infoForSuspend.causals.length === 1 && record) {
      setSelectedDescription({
        value: record.idWfCausal,
        label: record.causalName,
      });
    }
  }, [selectedReason, infoForSuspend]);

  const NavigationSection: React.FC<Partial<StepWizardChildProps>> = ({ ...props }) => {
    const handleNextStep: () => number = () => {
      if (props.currentStep === 1 && selectedReason.value === 'causale') {
        return 3;
      } else if (props.currentStep === 1 && selectedReason.value === 'info') {
        return 2;
      } else if (props.currentStep === 2 || props.currentStep === 3) {
        return 4;
      } else return 1;
    };
    const handleFinishModal = async () => {
      if (selectedReason.value === 'causale' && selectedDefinition && selectedDescription) {
        try {
          await services.openWorkFlow({
            idCausal: selectedDescription.value,
            idWorkflowDefinition: selectedDefinition.idDefinition,
            protocols,
            idCompany,
            idTaskDefinition: selectedTask?.idTaskDefinition,
            idGroups: selectedGroups.map((e) => e.idGroup),
            note,
          });
          dispatch(actions.documents.removeRows(protocols));
          dispatch(actions.modal.closeModal());
          utils.app.notify('success', t('sent_to_wf'));
          if (isEdit) dispatch(actions.documents.openNextDocument(history));
        } catch (e) {
          console.error(e);
          utils.app.notify('fail', e as Error);
        }
      } else if (selectedReason.value === 'info' && selectedWFDefinition) {
        try {
          await services.openWfInfo({
            protocols,
            idUser,
            idGroup: selectedGroups[0].idGroup,
            idWorkflowDefinition: selectedWFDefinition.idWorkflowDefinition,
            idCompany,
            note,
          });
          dispatch(actions.documents.removeRows(protocols));
          dispatch(actions.modal.closeModal());
          if (isEdit) dispatch(actions.documents.openNextDocument(history));
          utils.app.notify('success', t('sent_to_wf_info'));
        } catch (e) {
          console.error(e);
          utils.app.notify('fail', e as Error);
        }
      }
    };

    return (
      <Bottom customPadding="25px">
        <Modal.Footer
          dontCloseOnConfirm
          confirmAction={() =>
            props.goToStep && props.currentStep !== 4 ? props.goToStep(handleNextStep()) : handleFinishModal()
          }
          cancelAction={onCancel}
          confirmText={t('proceed')}
          confirmDisabled={
            selectedReason.value === 'causale'
              ? (props.currentStep === 1 && !selectedDescription) ||
                (props.currentStep === 3 && !selectedDefinition) ||
                (props.currentStep === 3 && selectedDefinition?.suspendType === 2 && !selectedTask) ||
                (props.currentStep === 4 && selectedDefinition?.noteMandatory && !note.trim().length)
              : selectedReason.value === 'info'
              ? (props.currentStep === 2 && !selectedWFDefinition) ||
                (props.currentStep === 2 && !selectedGroups.length)
              : false
          }
        />
      </Bottom>
    );
  };

  return infoForSuspend && groupsForInfo ? (
    <Container>
      <StepWizard
        className="wizard-selector"
        initialStep={1}
        nav={<NavigationSection />}
        transitions={animationOverride}
      >
        <InitialStep
          hashKey="1"
          infoForSuspend={infoForSuspend}
          dropOptOne={dropOptOne}
          selectedReason={selectedReason}
          setSelectedReason={setSelectedReason}
          selectedDescription={selectedDescription}
          setSelectedDescription={setSelectedDescription}
          infoInput={infoInput}
          setInfoInput={setInfoInput}
          setSelectedTask={updateSelectedTask}
          setSelectedGroups={setSelectedGroups}
          setSelectedDefinition={setSelectedDefinition}
          setSelectedWFDefinition={setSelectedWFDefinition}
          preSelectedDescription={modalProps?.data?.idCausal}
          noInfo={NO_INFO_SENDTOWF}
        />
        <InfoStep
          hashKey="2"
          dropOptOne={dropOptOne}
          selectedReason={selectedReason}
          infoInput={infoInput}
          definitions={groupsForInfo}
          setSelectedWFDefinition={setSelectedWFDefinition}
          setSelectedGroups={setSelectedGroups}
        />
        <CausalChoice
          hashKey="3"
          infoForSuspend={infoForSuspend}
          dropOptOne={dropOptOne}
          selectedReason={selectedReason}
          selectedDescription={selectedDescription}
          infoInput={infoInput}
          definitions={definitions}
          setSelectedTask={updateSelectedTask}
          setSelectedDefinition={setSelectedDefinition}
          setSelectedGroups={setSelectedGroups}
          preSelectedDefinition={preSelectedDefinition}
          preSelectedTask={preSelectedTask}
          preSelectedGroups={preSelectedGroups}
        />
        <Note
          hashKey="4"
          note={note}
          setNote={setNote}
          infoForSuspend={infoForSuspend}
          dropOptOne={dropOptOne}
          selectedReason={selectedReason}
          selectedDescription={selectedDescription}
          infoInput={infoInput}
        />
      </StepWizard>
    </Container>
  ) : null;
};

export default SendToWorkflow;
